import { useEffect, useMemo, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Search, X } from "lucide-react";
import { downloadCSV, inr, Kpi, EmptyRow } from "./utils";

export default function NewVsRepeatReport() {
  const [customers, setCustomers] = useState([]);
  const [customerFilter, setCustomerFilter] = useState("all");
  const [months, setMonths] = useState(12);
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/reports/customers").then(({ data }) => setCustomers(data)).catch(() => {});
  }, []);

  const load = () => {
    setBusy(true);
    const params = { months };
    if (customerFilter !== "all") params.customer_name = customerFilter;
    api.get("/reports/new-vs-repeat", { params })
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setBusy(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [customerFilter, months]);

  const rows = useMemo(() => {
    if (!data) return [];
    let list = data.rows;
    if (q.trim() && data.mode === "by-customer") {
      const n = q.toLowerCase();
      list = list.filter((r) => (r.customer_name || "").toLowerCase().includes(n));
    }
    return list;
  }, [data, q]);

  const totals = data?.totals || {};
  const mode = data?.mode; // by-month | by-customer

  const exportCSV = () => {
    if (mode === "by-month") {
      downloadCSV(
        `new-vs-repeat-${(data.customer || "customer").replace(/\s+/g, "-")}`,
        ["Customer Name", "Month", "Jobs Received", "Repeat Jobs Order", "New Jobs Order", "Repeat Jobs %"],
        rows.map((r) => [r.customer_name, r.month, r.jobs_received, r.repeat_jobs, r.new_jobs, `${r.repeat_pct}%`])
      );
    } else {
      downloadCSV(
        "new-vs-repeat-by-customer",
        ["Customer Name", "Jobs Received", "Repeat Jobs Order", "New Jobs Order", "Repeat Jobs %"],
        rows.map((r) => [r.customer_name, r.jobs_received, r.repeat_jobs, r.new_jobs, `${r.repeat_pct}%`])
      );
    }
  };

  const overallRepeat = totals.repeat_pct || 0;
  const repeatTone = overallRepeat >= 70 ? "ok" : overallRepeat >= 50 ? "warn" : "danger";

  // Max jobs for the mini bar in each row
  const maxRow = Math.max(1, ...rows.map((r) => r.jobs_received || 0));

  return (
    <div>
      <div className="border border-neutral-200 bg-white p-4 mb-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="label-caps block mb-1">Customer</label>
          <div className="flex items-center gap-1">
            <Select value={customerFilter} onValueChange={setCustomerFilter}>
              <SelectTrigger data-testid="nvr-customer" className="rounded-sm w-64"><SelectValue placeholder="All customers (rollup)" /></SelectTrigger>
              <SelectContent className="max-h-80">
                <SelectItem value="all">All customers (rollup)</SelectItem>
                {customers.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            {customerFilter !== "all" && (
              <button data-testid="nvr-clear-customer" onClick={() => setCustomerFilter("all")}
                className="p-1.5 hover:bg-neutral-100 rounded-sm" title="Clear"><X size={14} /></button>
            )}
          </div>
          <div className="text-[10px] text-neutral-500 mt-1">Pick a customer to see month-wise breakdown</div>
        </div>
        <div>
          <label className="label-caps block mb-1">Months</label>
          <Select value={String(months)} onValueChange={(v) => setMonths(Number(v))}>
            <SelectTrigger data-testid="nvr-months" className="rounded-sm w-28"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="6">6</SelectItem>
              <SelectItem value="12">12</SelectItem>
              <SelectItem value="18">18</SelectItem>
              <SelectItem value="24">24</SelectItem>
              <SelectItem value="36">36</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {mode === "by-customer" && (
          <div className="flex-1 min-w-48">
            <label className="label-caps block mb-1">Search customer</label>
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-2.5 text-neutral-400" />
              <Input data-testid="nvr-search" value={q} onChange={(e) => setQ(e.target.value)} className="pl-8 rounded-sm" placeholder="e.g. Hetero" />
            </div>
          </div>
        )}
        <Button data-testid="nvr-refresh" onClick={load} disabled={busy} variant="outline" className="rounded-sm">
          {busy ? "Loading…" : "Apply"}
        </Button>
        <Button data-testid="nvr-export" onClick={exportCSV} className="rounded-sm bg-[#FF5A00] hover:bg-[#e04f00]">
          Download .csv
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <Kpi label={mode === "by-month" ? `Jobs received (${months} months)` : "Total jobs received"} value={inr(totals.jobs_received || 0)} />
        <Kpi label="New jobs" value={inr(totals.new_jobs || 0)} tone="ok" />
        <Kpi label="Repeat jobs" value={inr(totals.repeat_jobs || 0)} />
        <Kpi label="Overall Repeat %" value={`${overallRepeat}%`} tone={repeatTone} />
      </div>

      <div className="border border-neutral-200 bg-white overflow-x-auto">
        {mode === "by-month" ? (
          <table className="w-full text-sm" data-testid="nvr-month-table">
            <thead className="bg-neutral-50 label-caps text-[10px]">
              <tr>
                <th className="text-left px-4 py-2.5">Customer Name</th>
                <th className="text-left px-4 py-2.5">Month</th>
                <th className="text-right px-4 py-2.5">Jobs Received</th>
                <th className="text-right px-4 py-2.5">Repeat Jobs Order</th>
                <th className="text-right px-4 py-2.5">New Jobs Order</th>
                <th className="text-right px-4 py-2.5">Repeat Jobs %</th>
                <th className="text-left px-4 py-2.5 w-56">Trend</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && <EmptyRow cols={7} msg="No monthly data for this customer." />}
              {rows.map((r, i) => {
                const tone = r.repeat_pct >= 70 ? "text-emerald-700"
                  : r.repeat_pct >= 50 ? "text-amber-700"
                  : "text-rose-700 font-semibold";
                const totalW = r.jobs_received ? (r.jobs_received / maxRow) * 100 : 0;
                const repeatW = r.jobs_received ? (r.repeat_jobs / r.jobs_received) * totalW : 0;
                const newW = totalW - repeatW;
                return (
                  <tr key={`${r.customer_name}-${r.month}`} className="border-t border-neutral-100 data-row" data-testid={`nvr-mrow-${r.month}`}>
                    <td className="px-4 py-2.5 font-medium">{i === 0 ? r.customer_name : ""}</td>
                    <td className="px-4 py-2.5 font-mono-num">{formatMonthLabel(r.month)}</td>
                    <td className="px-4 py-2.5 text-right font-mono-num font-semibold">{inr(r.jobs_received)}</td>
                    <td className="px-4 py-2.5 text-right font-mono-num text-neutral-800">{inr(r.repeat_jobs)}</td>
                    <td className="px-4 py-2.5 text-right font-mono-num text-emerald-700">{inr(r.new_jobs)}</td>
                    <td className={`px-4 py-2.5 text-right font-mono-num ${tone}`}>{r.repeat_pct}%</td>
                    <td className="px-4 py-2.5">
                      <div className="flex h-2 bg-neutral-50 border border-neutral-100 rounded-sm overflow-hidden" title={`Repeat ${r.repeat_jobs} · New ${r.new_jobs}`}>
                        <div className="bg-neutral-500" style={{ width: `${repeatW}%` }}></div>
                        <div className="bg-emerald-500" style={{ width: `${newW}%` }}></div>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {rows.length > 0 && (
                <tr className="border-t-2 border-neutral-300 bg-neutral-50 font-bold" data-testid="nvr-month-total-row">
                  <td className="px-4 py-2.5"></td>
                  <td className="px-4 py-2.5 label-caps text-[10px]">TOTAL</td>
                  <td className="px-4 py-2.5 text-right font-mono-num">{inr(totals.jobs_received)}</td>
                  <td className="px-4 py-2.5 text-right font-mono-num text-neutral-800">{inr(totals.repeat_jobs)}</td>
                  <td className="px-4 py-2.5 text-right font-mono-num text-emerald-700">{inr(totals.new_jobs)}</td>
                  <td className="px-4 py-2.5 text-right font-mono-num text-[#FF5A00]">{totals.repeat_pct}%</td>
                  <td></td>
                </tr>
              )}
            </tbody>
          </table>
        ) : (
          <table className="w-full text-sm" data-testid="nvr-customer-table">
            <thead className="bg-neutral-50 label-caps text-[10px]">
              <tr>
                <th className="text-left px-4 py-2.5">Customer Name</th>
                <th className="text-right px-4 py-2.5">Jobs Received</th>
                <th className="text-right px-4 py-2.5">Repeat Jobs Order</th>
                <th className="text-right px-4 py-2.5">New Jobs Order</th>
                <th className="text-right px-4 py-2.5">Repeat Jobs %</th>
                <th className="text-left px-4 py-2.5 w-56">Split</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && <EmptyRow cols={6} msg="No customers with jobs." />}
              {rows.slice(0, 400).map((r) => {
                const tone = r.repeat_pct >= 70 ? "text-emerald-700"
                  : r.repeat_pct >= 50 ? "text-amber-700"
                  : "text-rose-700 font-semibold";
                const totalW = r.jobs_received ? (r.jobs_received / maxRow) * 100 : 0;
                const repeatW = r.jobs_received ? (r.repeat_jobs / r.jobs_received) * totalW : 0;
                const newW = totalW - repeatW;
                return (
                  <tr key={r.customer_name} className="border-t border-neutral-100 data-row" data-testid={`nvr-crow-${r.customer_name}`}>
                    <td className="px-4 py-2.5 font-medium">
                      <button className="text-left hover:text-[#FF5A00]" onClick={() => setCustomerFilter(r.customer_name)}
                        data-testid={`nvr-drill-${r.customer_name}`}>
                        {r.customer_name}
                      </button>
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono-num font-semibold">{inr(r.jobs_received)}</td>
                    <td className="px-4 py-2.5 text-right font-mono-num">{inr(r.repeat_jobs)}</td>
                    <td className="px-4 py-2.5 text-right font-mono-num text-emerald-700">{inr(r.new_jobs)}</td>
                    <td className={`px-4 py-2.5 text-right font-mono-num ${tone}`}>{r.repeat_pct}%</td>
                    <td className="px-4 py-2.5">
                      <div className="flex h-2 bg-neutral-50 border border-neutral-100 rounded-sm overflow-hidden" title={`Repeat ${r.repeat_jobs} · New ${r.new_jobs}`}>
                        <div className="bg-neutral-500" style={{ width: `${repeatW}%` }}></div>
                        <div className="bg-emerald-500" style={{ width: `${newW}%` }}></div>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {rows.length > 400 && (
                <tr><td colSpan={6} className="px-4 py-3 text-center text-neutral-400 text-xs">Showing first 400 of {rows.length} rows. Export CSV for full list.</td></tr>
              )}
              <tr className="border-t-2 border-neutral-300 bg-neutral-50 font-bold" data-testid="nvr-customer-total-row">
                <td className="px-4 py-2.5 label-caps text-[10px]">TOTAL</td>
                <td className="px-4 py-2.5 text-right font-mono-num">{inr(totals.jobs_received)}</td>
                <td className="px-4 py-2.5 text-right font-mono-num">{inr(totals.repeat_jobs)}</td>
                <td className="px-4 py-2.5 text-right font-mono-num text-emerald-700">{inr(totals.new_jobs)}</td>
                <td className="px-4 py-2.5 text-right font-mono-num text-[#FF5A00]">{totals.repeat_pct}%</td>
                <td></td>
              </tr>
            </tbody>
          </table>
        )}
      </div>
      <p className="mt-3 text-xs text-neutral-500">
        Repeat Jobs % = Repeat / Total × 100 · Green ≥ 70% (healthy loyalty) · Amber 50-70% · Red &lt; 50%.
        {mode === "by-customer" && <> Click a customer name to drill into month-wise view.</>}
      </p>
    </div>
  );
}

function formatMonthLabel(m) {
  // "2024-04" → "April 2024"
  if (!m || m.length < 7) return m;
  const [y, mo] = m.split("-");
  const names = ["January","February","March","April","May","June","July","August","September","October","November","December"];
  return `${names[Number(mo) - 1] || mo} ${y}`;
}


